import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Divider,
  useTheme,
  alpha,
} from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import { Formik } from "formik";
import * as yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { AuthContext } from "../../components/AuthContext";
import config from "../../config";
import { useModules } from "../../context/ModuleContext";
import { firebaseAuth, googleProvider, isFirebaseLoginEnabled } from "../../utils/firebaseAuth";
import { signInWithPopup } from "firebase/auth";
import { jwtDecode } from "jwt-decode";
import { tokens } from "../../theme";

const TEBA_LOGO = `${process.env.PUBLIC_URL}/assets/logo.jpg`;
const HPU_LOGO = `${process.env.PUBLIC_URL}/images/teba-hpu-logo.png`;

const Login = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const location = useLocation();
  const { updateAuth } = useContext(AuthContext);
  const { refreshModules } = useModules();
  const [googleLoading, setGoogleLoading] = useState(false);
  const returnTo = location.state?.from || null;

  const resolveNextPathFromToken = (token, responseData) => {
    if (returnTo) return returnTo;
    if (responseData?.next_path) return responseData.next_path;
    try {
      const decoded = jwtDecode(token);
      if (decoded.role === "hpc_admin") return "/hpc/members";
      if (decoded.role === "member" || decoded.principal_type === "member") return "/view_member";
    } catch (error) {
      console.error("Failed to decode token for redirect:", error);
    }
    return "/home";
  };

  const resolveNextPath = (responseData, token) =>
    resolveNextPathFromToken(token || responseData?.access_token, responseData);

  const handleFormSubmit = async (values, { resetForm }) => {
    const { username, password } = values;
    try {
      const response = await axios.post(`${config.apiBaseUrl}/login`, {
        username,
        password,
      });
      const token = response.data.access_token;

      toast.success("Login successful");
      updateAuth(token);
      refreshModules();
      resetForm();
      navigate(resolveNextPathFromToken(token));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || "Login failed");
    }
  };

  const handleGoogleLogin = async () => {
    if (!isFirebaseLoginEnabled || !firebaseAuth || !googleProvider) {
      toast.error("Google login is not configured");
      return;
    }

    setGoogleLoading(true);
    try {
      const result = await signInWithPopup(firebaseAuth, googleProvider);
      const idToken = await result.user.getIdToken();
      const response = await axios.post(`${config.apiBaseUrl}/auth/firebase-login`, {
        idToken,
      });

      updateAuth(response.data.access_token, response.data);
      refreshModules();

      try {
        await result.user.getIdToken(true);
      } catch (refreshError) {
        console.error("Firebase token refresh failed:", refreshError);
      }

      toast.success("Google login successful");
      navigate(resolveNextPath(response.data, response.data.access_token));
    } catch (err) {
      console.error(err);
      toast.error(err.response?.data?.error || err.message || "Google login failed");
      if (firebaseAuth?.currentUser) {
        try {
          await firebaseAuth.signOut();
        } catch (signOutError) {
          console.error("Firebase cleanup sign-out failed:", signOutError);
        }
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const initialValues = {
    username: "",
    password: "",
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
        py: { xs: 3, sm: 4 },
        background: `
          radial-gradient(ellipse 80% 50% at 50% -10%, ${alpha(colors.greenAccent[500], 0.14)} 0%, transparent 55%),
          radial-gradient(ellipse 60% 40% at 100% 80%, ${alpha(colors.blueAccent[500], 0.1)} 0%, transparent 50%),
          linear-gradient(180deg, ${colors.primary[500]} 0%, ${colors.primary[600]} 100%)
        `,
      }}
    >
      <ToastContainer />
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 440,
          p: { xs: 3, sm: 4 },
          borderRadius: 3,
          backgroundColor: alpha(colors.primary[400], theme.palette.mode === "dark" ? 0.55 : 0.92),
          backdropFilter: "blur(12px)",
          border: `1px solid ${alpha(colors.grey[100], 0.1)}`,
          boxShadow: `0 24px 48px ${alpha("#000", 0.35)}`,
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          mb={3}
          gap={2}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            gap={{ xs: 1.5, sm: 2 }}
            flexWrap="wrap"
          >
            <Box
              component="img"
              src={TEBA_LOGO}
              alt="TEBA"
              sx={{
                height: { xs: 48, sm: 56 },
                width: { xs: 48, sm: 56 },
                borderRadius: "50%",
                objectFit: "cover",
                border: `2px solid ${colors.greenAccent[500]}`,
                boxShadow: `0 4px 12px ${alpha(colors.greenAccent[500], 0.25)}`,
              }}
            />
            <Box
              component="img"
              src={HPU_LOGO}
              alt="TEBA Health & Performance Unit"
              sx={{
                height: { xs: 40, sm: 48 },
                width: "auto",
                maxWidth: { xs: 120, sm: 150 },
                objectFit: "contain",
              }}
            />
          </Box>
          <Box textAlign="center">
            <Typography
              variant="h4"
              fontWeight="bold"
              color={colors.grey[100]}
              sx={{ fontSize: { xs: "1.5rem", sm: "2rem" } }}
            >
              Welcome back
            </Typography>
            <Typography
              variant="body2"
              color={colors.greenAccent[400]}
              sx={{ mt: 0.5 }}
            >
              Enter your username and password
            </Typography>
          </Box>
        </Box>

        <Formik
          onSubmit={handleFormSubmit}
          initialValues={initialValues}
          validationSchema={checkoutSchema}
        >
          {({
            values,
            errors,
            touched,
            handleBlur,
            setFieldValue,
            handleSubmit,
          }) => (
            <form onSubmit={handleSubmit}>
              <Box display="flex" flexDirection="column" gap={2.5}>
                <TextField
                  fullWidth
                  variant="filled"
                  type="text"
                  label="Username"
                  onBlur={handleBlur}
                  onChange={(e) => setFieldValue("username", e.target.value)}
                  value={values.username}
                  name="username"
                  error={!!touched.username && !!errors.username}
                  helperText={touched.username && errors.username}
                  autoComplete="username"
                />
                <TextField
                  fullWidth
                  variant="filled"
                  type="password"
                  label="Password"
                  onBlur={handleBlur}
                  onChange={(e) => setFieldValue("password", e.target.value)}
                  value={values.password}
                  name="password"
                  error={!!touched.password && !!errors.password}
                  helperText={touched.password && errors.password}
                  autoComplete="current-password"
                />
                <Button
                  type="submit"
                  color="secondary"
                  variant="contained"
                  size="large"
                  fullWidth
                  sx={{
                    mt: 0.5,
                    py: 1.25,
                    fontWeight: 600,
                    boxShadow: `0 4px 14px ${alpha(colors.greenAccent[500], 0.35)}`,
                    "&:hover": {
                      boxShadow: `0 6px 20px ${alpha(colors.greenAccent[500], 0.45)}`,
                    },
                  }}
                >
                  Login
                </Button>

                {config.enableGoogleLogin && (
                  <>
                    <Divider sx={{ my: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        or continue with
                      </Typography>
                    </Divider>
                    <Button
                      type="button"
                      variant="contained"
                      size="large"
                      fullWidth
                      onClick={handleGoogleLogin}
                      startIcon={<GoogleIcon />}
                      disabled={googleLoading}
                      sx={{
                        py: 1.25,
                        backgroundColor: "#ffffff",
                        color: "#1f2937",
                        border: "1px solid #d0d5dd",
                        justifyContent: "center",
                        boxShadow: "none",
                        "&:hover": {
                          backgroundColor: "#f8fafc",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                        },
                        "&.Mui-disabled": {
                          backgroundColor: "#e5e7eb",
                          color: "#6b7280",
                        },
                      }}
                    >
                      {googleLoading ? "Signing in with Google..." : "Continue with Google"}
                    </Button>
                  </>
                )}
              </Box>
            </form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

const checkoutSchema = yup.object().shape({
  username: yup.string().required("required"),
  password: yup.string().required("required"),
});

export default Login;
